import type { ProposedAction } from './domain';

export type CockpitTransport = 'app' | 'voice' | 'phone' | 'telegram' | 'web';
export type CockpitContext = 'personal' | 'work' | 'world' | 'build';
export type CockpitPrivacy = 'private' | 'shared' | 'public';
export type CockpitModality = 'text' | 'voice' | 'image' | 'video' | 'pdf' | 'link' | 'sensor';

export type CockpitInput = {
  id: string;
  transport: CockpitTransport;
  context: CockpitContext;
  modality: CockpitModality;
  contentRef: string;
  owner: string;
  purpose: string;
  privacy: CockpitPrivacy;
  sourceRef: string;
  evidenceRefs: string[];
  createdAt: string;
};

export type CockpitRoute = {
  target: 'Personal OS' | 'Work OS' | 'World OS' | 'Software Factory';
  adapter: 'personal-profile' | 'work-operator' | 'olav-kernel' | 'factory-workflow';
  eventKind: 'profile-event' | 'work-event' | 'olav-semantic-observation' | 'build-request';
};

export type CockpitEnvelope = CockpitInput & {
  schema: 'valo.cockpit.input.v1';
  route: CockpitRoute;
  receiptRequired: true;
  authorizationBoundary: 'REHT';
  executionAuthority: 'NONE';
  promoteRequired: boolean;
  canonicalEvent: {
    eventId: string;
    tenantId: string;
    source: 'unified-cockpit';
    subject: string;
    payload: Record<string, unknown>;
    evidenceRefs: string[];
    idempotencyKey: string;
  };
};

const ROUTES: Record<CockpitContext, CockpitRoute> = {
  personal: {
    target: 'Personal OS',
    adapter: 'personal-profile',
    eventKind: 'profile-event',
  },
  work: {
    target: 'Work OS',
    adapter: 'work-operator',
    eventKind: 'work-event',
  },
  world: {
    target: 'World OS',
    adapter: 'olav-kernel',
    eventKind: 'olav-semantic-observation',
  },
  build: {
    target: 'Software Factory',
    adapter: 'factory-workflow',
    eventKind: 'build-request',
  },
};

function requireText(value: string, label: string) {
  if (!value.trim()) {
    throw new Error(`${label} is required`);
  }
}

export function routeCockpitInput(input: CockpitInput): CockpitEnvelope {
  requireText(input.id, 'id');
  requireText(input.owner, 'owner');
  requireText(input.purpose, 'purpose');
  requireText(input.contentRef, 'contentRef');
  requireText(input.sourceRef, 'sourceRef');

  const route = ROUTES[input.context];
  const promoteRequired = input.context !== 'personal' || input.privacy !== 'private';

  return {
    ...input,
    schema: 'valo.cockpit.input.v1',
    route,
    receiptRequired: true,
    authorizationBoundary: 'REHT',
    executionAuthority: 'NONE',
    promoteRequired,
    canonicalEvent: {
      eventId: `cockpit:${input.id}`,
      tenantId: input.context,
      source: 'unified-cockpit',
      subject: input.contentRef,
      payload: {
        transport: input.transport,
        modality: input.modality,
        context: input.context,
        privacy: input.privacy,
        owner: input.owner,
        purpose: input.purpose,
        adapter: route.adapter,
      },
      evidenceRefs: input.evidenceRefs,
      idempotencyKey: `cockpit:${input.context}:${input.id}`,
    },
  };
}

export function buildWorldSemanticObservation(input: CockpitInput) {
  const envelope = routeCockpitInput(input);
  if (envelope.context !== 'world') {
    throw new Error('world semantic observation requires world context');
  }

  return {
    observationId: envelope.id,
    subject: envelope.contentRef,
    predicate: 'cockpit_input',
    object: envelope.modality,
    owner: envelope.owner,
    purpose: envelope.purpose,
    context: 'world' as const,
    privacy: envelope.privacy,
    sourceId: envelope.sourceRef,
    sourceType: envelope.transport,
    sourceContext: envelope.route.adapter,
    evidenceRefs: envelope.evidenceRefs,
    observedAt: envelope.createdAt,
  };
}

export function proposePromoteShare(envelope: CockpitEnvelope): ProposedAction | undefined {
  if (!envelope.promoteRequired) {
    return undefined;
  }

  return {
    id: `promote:${envelope.id}`,
    principalId: envelope.owner,
    profile: envelope.context === 'personal' ? 'personal' : 'professional',
    capability: 'SHARE_DATA',
    purpose: `Promote cockpit input to ${envelope.route.target}`,
    amountMinor: 0,
    currency: 'NOK',
    recipient: envelope.route.target,
    channel: 'personal-agency-os',
    risk: envelope.privacy === 'public' ? 'medium' : 'low',
    reversible: false,
    external: envelope.context !== 'personal',
    dataScopes: [envelope.modality, envelope.privacy],
    evidence: envelope.evidenceRefs,
  };
}
